import Analytics from './Analytics';
import type Tracker from './trackers/Tracker';
import { getGlobalLogger } from '@zg-rentals/logger-base';

interface MultiAnalyticsInterface {
  ids: Array<string>;
  drymode?: boolean;
  anonymousId?: string;
}

type MultiAnalyticsCallback = (s: string) => void;
type TrackerArguments = Record<string, unknown>;

/**
 * @description Manage multiple GTM and UA profiles with a simple interface.
 * @class MultiAnalytics
 */
export default class MultiAnalytics {
  public analytics: Array<Analytics>;
  public drymode: boolean;
  public logger = getGlobalLogger();

  /**
   * Creates an instance of MultiAnalytics.
   * @param  {object} options MultiAnalytics options
   * @param {string[]} [options.ids] Array of GTM and/or UA profile ids
   * @param {boolean} [options.drymode=false] Set to true to prevent actual analytic tracking
   * @example
   * // setup multi tracker for ua and gtm
   * import { MultiAnalytics, trackerIds } from '@zg-rentals/analytics';
   * const ma = new MultiAnalytics({
   *  ids: [
   *    trackerIds.ids.ua.multibrand,
   *    trackerIds.ids.gtm.dev
   *  ]
   * })
   * @memberof MultiAnalytics
   */
  constructor(args: MultiAnalyticsInterface) {
    const { anonymousId, drymode = false, ids } = args;

    this.drymode = drymode;
    this.logger = getGlobalLogger();
    this.analytics = ids.map(
      (id) =>
        new Analytics({
          anonymousId,
          id,
          drymode,
        }),
    );
  }

  getMatchingTracker(id: string): Analytics | undefined {
    let matchedTracker: Analytics | undefined;

    this.analytics.forEach((tracker) => {
      if (tracker.id === id) {
        matchedTracker = tracker;
      }
    });

    return matchedTracker;
  }

  /**
   * @description Generates all tracker code to be included in html body
   * @return {string} String of concatenated script tags to include in <body>
   * @example
   * renderToString(`
   *  <html>
   *    <body>
   *      ${ma.renderHtmlInBody()}
   *    </body>
   *  </html>
   * `);
   * @memberof MultiAnalytics
   */
  renderHtmlInBody() {
    let html = '';

    this.analytics.forEach((a) => {
      html += a.tracker.renderHtmlInBody();
    });

    return html;
  }

  /**
   * @description Generates all tracker code to be included in html head
   * @param  {string|object} options ga options for ua script
   * @return {string} String of concatenated script tags to include in <head>
   * @example
   * renderToString(`
   *  <html>
   *    <head>
   *      ${ma.renderHtmlInHead('auto')}
   *    </head>
   *  </html>
   * `);
   * @memberof MultiAnalytics
   */
  renderHtmlInHead(options: Record<string, unknown>) {
    let html = '';

    this.analytics.forEach((a) => {
      html += a.tracker.renderHtmlInHead(options);
    });

    return html;
  }

  /**
   * @description CCPA compliance, disables trackers based on optanon cookie settings
   * @example
   * // client/index.js
   * ma.stopDisabledTrackers();
   * @memberof MultiAnalytics
   */
  stopDisabledTrackers = () => {
    this.analytics.forEach((a) => {
      a.tracker.stopDisabledTracker();
    });
  };

  _executeTrackerFunc(
    tracker: Tracker,
    funcName: string,
    args: TrackerArguments,
    cb: MultiAnalyticsCallback = () => {},
  ) {
    if (!tracker) {
      this.logger && this.logger.warn('missing tracker, cannot execute func');
    } else {
      const trackerFunc = tracker[funcName as keyof Tracker];
      if (typeof trackerFunc === 'function') {
        trackerFunc(args, cb);
      } else {
        this.logger && this.logger.warn(`tracker: ${tracker.type} has no method for ${funcName}, skipping`);
      }
    }
  }

  _commandWithGtmFilter(
    tracker: Tracker,
    funcName: string,
    args: TrackerArguments,
    cb: MultiAnalyticsCallback = () => {},
    state: Record<string, unknown>,
  ) {
    const { hasGtmTracked } = state;

    if (tracker.type !== 'gtm') {
      this.logger &&
        this.logger.debug(
          {
            funcName,
          },
          'tracking non gtm command',
        );
      this._executeTrackerFunc(tracker, funcName, args, cb);
    } else if (!hasGtmTracked) {
      this.logger &&
        this.logger.debug(
          {
            funcName,
          },
          'no gtm command issued, executing gtm command',
        );
      state.hasGtmTracked = true;
      this._executeTrackerFunc(tracker, funcName, args, cb);
    } else {
      this.logger &&
        this.logger.debug(
          {
            funcName,
          },
          'gtm command already issued, skipping gtm command',
        );
    }
  }

  _command = (
    funcName: string,
    args: TrackerArguments,
    trackerIds: Array<string | null | undefined>,
    cb: MultiAnalyticsCallback = () => {},
  ) => {
    // only need to track pageviews / events for GTM a single time.
    // use object so we pass by reference rather than value to helper func
    const state = {
      hasGtmTracked: false,
    };

    // TODO: add support for comscore
    if (Array.isArray(trackerIds) && trackerIds.length > 0) {
      trackerIds.forEach((id) => {
        if (id) {
          const instance = this.getMatchingTracker(id);

          if (instance) {
            this._commandWithGtmFilter(instance.tracker, funcName, args, cb, state);
          }
        }
      });
    } else {
      this.analytics.forEach((instance) => {
        const tracker = instance.tracker;

        this._commandWithGtmFilter(tracker, funcName, args, cb, state);
      });
    }
  };

  /**
   * @description Track page view across all gtm and ua trackers. This corresponds to the 'send' command in ua, and the comet_page_view_event in gtm
   * @param  {objects} args additional args to send with page tracking
   * @param {array} [trackerIds] array of gtm/ua trackerIds to set custom dimensions for. If none provided, will set for all trackerIds.
   * @param {func} cb callback to execute after tracking page view. Returns id of tracker.
   * @return {void}
   * @example
   * // track the current page
   * ma.trackPageView({
   *   path: window.location.pathname
   * });
   * @example
   * // track the current page for a specific trackerId
   * ma.trackPageView({
   *   path: window.location.pathname
   * }, ['UA-21174015-74']);
   * @memberof MultiAnalytics
   */
  trackPageView(args: TrackerArguments, trackerIds: Array<string>, cb: MultiAnalyticsCallback = () => {}) {
    this._command('trackPageView', args, trackerIds, cb);
  }

  /**
   * @description Track event across all gtm and ua trackers
   * @param  {object} args additional args to send with event tracking
   * @param {array} [trackerIds] array of gtm/ua trackerIds to set custom dimensions for. If none provided, will set for all trackerIds.
   * @param {func} cb callback to execute after tracking page view. Returns id of tracker.
   * @return {void}
   * @example
   * // send event
   * ma.trackEvent({
   *   category: 'Mortgages',
   *   action: 'Upsell clicked',
   *   label: 'Search results list'
   * });
   * @example
   * // send event to a specific trackerId
   * ma.trackEvent({
   *   category: 'Mortgages',
   *   action: 'Upsell clicked',
   *   label: 'Search results list'
   * }, ['UA-21174015-74']);
   * @memberof MultiAnalytics
   */
  trackEvent(args: TrackerArguments, trackerIds: Array<string>, cb: MultiAnalyticsCallback = () => {}) {
    this._command('trackEvent', args, trackerIds, cb);
  }

  /**
   * @description Set custom dimensions across gtm and all ua trackers. These dimensions must correspond to dimensions setup in ua / gtm!
   * @param  {object} args custom dimension args
   * @param {string} [args.name] name of custom dimension
   * @param {any} [args.value] value of custom dimension
   * @param {array} [trackerIds] array of gtm/ua trackerIds to set custom dimensions for. If none provided, will set for all trackerIds.
   * @param {func} cb callback to execute after tracking page view. Returns id of tracker.
   * @return {void}
   * @example
   * // set custom dimension across all trackers
   * ma.setCustomDimensions({
   *   name: 'dimension25',
   *   value: 'mobile'
   * });
   * @example
   * // set custom dimension for a single tracker
   * ma.setCustomDimensions({
   *  name: 'dimension25',
   *  value: 'mobile'
   * }, ['UA-21174015-74']);
   * @memberof MultiAnalytics
   */
  setCustomDimensions = (
    args: TrackerArguments,
    trackerIds: Array<string | null | undefined>,
    cb: MultiAnalyticsCallback = () => {},
  ) => {
    this._command('setCustomDimensions', args, trackerIds, cb);
  };
}
